import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Status} from '@domain/response-status';
import {GenericAuthService} from '@infrastructure/generic-auth/generic-auth.service';
import {RequestStatus} from '@infrastructure/request-status.enum';
import {TranslateService} from '@ngx-translate/core';
import {catchError, EMPTY, Subject, switchMap} from 'rxjs';
import {FileAreas} from 'src/domain/enum/file-areas.enum';
import {SnackBarService} from 'src/shared/snackbar/snackbar.service';

interface RequestParams
{
    id?: string;
    area: FileAreas;
    qrCodeToken?: string;
}

interface FileInstance
{
    name: string;
    mimeType: string;
}

@Injectable({
    providedIn: 'root'
})
export class FilesService
{
    private readonly httpClient = inject(HttpClient);

    private readonly snackBar = inject(SnackBarService);

    private readonly translate = inject(TranslateService);

    private readonly genericAuthService = inject(GenericAuthService);

    private readonly get$ = new Subject<RequestParams>();

    private readonly state = signal<Status & {fileId?: string}>({
        status: RequestStatus.NOT_EXECUTED
    });

    public readonly loading = computed(() => this.state().status === RequestStatus.PENDING);

    public readonly currentFileId = computed(() => this.state().fileId);

    private readonly file = signal<FileInstance>(undefined);

    private readonly fetchDetails$ = this.get$.pipe(
        switchMap(({id, area, qrCodeToken}) =>
        {
            return this.httpClient.get('files', {params: {id, area, ...!this.genericAuthService.isAuthenticated() ? {qrCodeToken} : {}}, responseType: 'arraybuffer'}).pipe(
                catchError(() =>
                {
                    this.state.set({
                        status: RequestStatus.ERROR
                    });

                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.GENERIC'));

                    return EMPTY;
                })
            );
        })
    );

    public constructor()
    {
        this.fetchDetails$.pipe(takeUntilDestroyed()).subscribe({
            next: (response: ArrayBuffer) =>
            {
                this.state.set({
                    status: RequestStatus.CONCLUDED
                });

                const blob = new Blob([response], { type: this.file().mimeType });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = this.file().name;
                a.click();
                URL.revokeObjectURL(url);
            }
        });
    }

    public getFile(file: FileInstance, params: RequestParams): void
    {
        this.file.set(file);
        this.state.set({
            status: RequestStatus.PENDING,
            fileId: params.id
        });
        this.get$.next(params);
    }
}
